import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

/**
 * Recent print jobs across every registered printer.
 *
 * Two sections:
 *   - Printers: last-heartbeat freshness for each installed agent.
 *   - Jobs: the last 100 print jobs with their status, attempts, and
 *           a Retry button on FAILED rows.
 *
 * Super Admin only, gated at the route level in App.tsx (perm=VIEW_OVERSIGHT).
 */

interface Printer {
  PrinterID: string;
  DisplayName: string | null;
  Location: string | null;
  Enabled: boolean;
  LastHeartbeatOn: string | null;
  LastAgentVersion: string | null;
  CreatedOn: string;
}

interface Job {
  JobID: string;
  PrinterID: string;
  TemplateName: string;
  Status: 'QUEUED' | 'LEASED' | 'PRINTED' | 'FAILED' | 'CANCELLED';
  CreatedOn: string;
  PrintedOn: string | null;
  Attempts: number;
  LastError: string | null;
  LeaseExpiresOn: string | null;
  CreatedByName: string | null;
  Copies: number;
  JobNumber: string | null;
  SlipCode: string | null;
}

const HEARTBEAT_STALE_MS = 90_000;

function isStale(iso: string | null): boolean {
  if (!iso) return true;
  return Date.now() - new Date(iso).getTime() > HEARTBEAT_STALE_MS;
}

function statusBadge(s: Job['Status']): string {
  switch (s) {
    case 'QUEUED':    return 'bg-slate-100 text-slate-700';
    case 'LEASED':    return 'bg-blue-100 text-blue-700';
    case 'PRINTED':   return 'bg-green-100 text-green-700';
    case 'FAILED':    return 'bg-red-100 text-red-700';
    case 'CANCELLED': return 'bg-amber-100 text-amber-700';
  }
}

export function PrintLogPage() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<'' | Job['Status']>('');
  const [printerFilter, setPrinterFilter] = useState('');

  const printers = useQuery({
    queryKey: ['print-agents'],
    queryFn: () => api<{ rows: Printer[] }>('/api/print/agents'),
    refetchInterval: 30_000,
  });

  const jobs = useQuery({
    queryKey: ['print-jobs', statusFilter, printerFilter],
    queryFn: () => {
      const p = new URLSearchParams();
      p.set('limit', '100');
      if (statusFilter)  p.set('status',    statusFilter);
      if (printerFilter) p.set('printerId', printerFilter);
      return api<{ rows: Job[] }>(`/api/print/jobs?${p.toString()}`);
    },
    refetchInterval: 5_000,
  });

  const retry = useMutation({
    mutationFn: (jobId: string) =>
      api(`/api/print/jobs/${jobId}/retry`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['print-jobs'] }),
  });

  const cancel = useMutation({
    mutationFn: (jobId: string) =>
      api(`/api/print/jobs/${jobId}/cancel`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['print-jobs'] }),
  });

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-brand mb-1">Print Log</h1>
        <p className="text-sm text-slate-500">
          Label printer status, and the last 100 print jobs. Auto-refreshes every 5 seconds.
        </p>
      </div>

      <section className="bg-white border rounded-lg overflow-hidden">
        <div className="px-4 py-2 border-b bg-slate-50 text-xs font-semibold text-slate-600 uppercase tracking-wider">
          Printers
        </div>
        {(printers.data?.rows ?? []).length === 0 ? (
          <div className="p-4 text-sm text-slate-500">
            No printer agents registered yet.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-4 py-2">Printer</th>
                <th className="text-left px-4 py-2">Location</th>
                <th className="text-left px-4 py-2">Last Heartbeat</th>
                <th className="text-left px-4 py-2">Agent Version</th>
                <th className="text-left px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {(printers.data?.rows ?? []).map(p => {
                const stale = isStale(p.LastHeartbeatOn);
                return (
                  <tr key={p.PrinterID} className="border-t">
                    <td className="px-4 py-2 font-medium">
                      <div>{p.DisplayName || p.PrinterID}</div>
                      <div className="text-[10px] text-slate-400">{p.PrinterID}</div>
                    </td>
                    <td className="px-4 py-2 text-slate-600">{p.Location || '—'}</td>
                    <td className="px-4 py-2 text-slate-600">
                      {p.LastHeartbeatOn ? new Date(p.LastHeartbeatOn).toLocaleString() : 'never'}
                    </td>
                    <td className="px-4 py-2 text-slate-600">{p.LastAgentVersion || '—'}</td>
                    <td className="px-4 py-2">
                      {!p.Enabled ? (
                        <span className="text-xs bg-slate-200 text-slate-700 px-2 py-0.5 rounded">disabled</span>
                      ) : stale ? (
                        <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">offline</span>
                      ) : (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">online</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      <section className="bg-white border rounded-lg overflow-hidden">
        <div className="px-4 py-2 border-b bg-slate-50 flex items-center justify-between">
          <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Jobs</div>
          <div className="flex items-center gap-2">
            <select
              className="text-xs border rounded px-2 py-1"
              value={printerFilter}
              onChange={e => setPrinterFilter(e.target.value)}
            >
              <option value="">All printers</option>
              {(printers.data?.rows ?? []).map(p => (
                <option key={p.PrinterID} value={p.PrinterID}>{p.DisplayName || p.PrinterID}</option>
              ))}
            </select>
            <select
              className="text-xs border rounded px-2 py-1"
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value as '' | Job['Status'])}
            >
              <option value="">All statuses</option>
              <option value="QUEUED">Queued</option>
              <option value="LEASED">Printing</option>
              <option value="PRINTED">Printed</option>
              <option value="FAILED">Failed</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left px-4 py-2">Created</th>
              <th className="text-left px-4 py-2">Job No.</th>
              <th className="text-left px-4 py-2">Slip</th>
              <th className="text-left px-4 py-2">Copies</th>
              <th className="text-left px-4 py-2">Printer</th>
              <th className="text-left px-4 py-2">By</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-left px-4 py-2">Attempts</th>
              <th className="text-left px-4 py-2">Detail</th>
              <th className="text-right px-4 py-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {(jobs.data?.rows ?? []).map(j => (
              <tr key={j.JobID} className="border-t">
                <td className="px-4 py-2 text-slate-600">{new Date(j.CreatedOn).toLocaleString()}</td>
                <td className="px-4 py-2 font-medium text-slate-800">{j.JobNumber || '—'}</td>
                <td className="px-4 py-2 text-slate-600">{j.SlipCode || '—'}</td>
                <td className="px-4 py-2 text-slate-700 font-medium">{j.Copies}</td>
                <td className="px-4 py-2 text-slate-700">{j.PrinterID}</td>
                <td className="px-4 py-2 text-slate-600">{j.CreatedByName || '—'}</td>
                <td className="px-4 py-2">
                  <span className={`text-xs px-2 py-0.5 rounded ${statusBadge(j.Status)}`}>
                    {j.Status}
                  </span>
                </td>
                <td className="px-4 py-2 text-slate-600">{j.Attempts}</td>
                <td className="px-4 py-2 text-xs text-slate-500 max-w-xs truncate" title={j.LastError ?? ''}>
                  {j.Status === 'PRINTED' && j.PrintedOn
                    ? `printed ${new Date(j.PrintedOn).toLocaleString()}`
                    : (j.LastError || '—')}
                </td>
                <td className="px-4 py-2 text-right">
                  {j.Status === 'FAILED' && (
                    <button
                      className="text-xs text-blue-600 hover:underline mr-3"
                      onClick={() => retry.mutate(j.JobID)}
                      disabled={retry.isPending}
                    >
                      Retry
                    </button>
                  )}
                  {(j.Status === 'QUEUED' || j.Status === 'LEASED' || j.Status === 'FAILED') && (
                    <button
                      className="text-xs text-red-600 hover:underline"
                      onClick={() => cancel.mutate(j.JobID)}
                      disabled={cancel.isPending}
                    >
                      {j.Status === 'LEASED' ? 'Stop' : 'Cancel'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {(jobs.data?.rows ?? []).length === 0 && !jobs.isLoading && (
              <tr><td colSpan={10} className="p-6 text-center text-slate-400">No print jobs yet.</td></tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}
